/**
 * Native-feel pull-to-refresh for the Capacitor shell.
 *
 * The app's main scroller is the inner `#main_content` div (not the web view's
 * own scroll view), so a native iOS/Android refresh control can't drive this —
 * it has to be detected in JS on that element. Pulling down while already at the
 * top reveals a spinner; releasing past a threshold re-fetches the current
 * page's data via Inertia `router.reload()` (a background props refresh — no
 * full SPA reload, no flash).
 *
 * Regression safety: gated on the Capacitor shell, so browser + MobiLoud are
 * completely untouched. The touch handlers engage ONLY when the scroller is at
 * the very top and the drag is downward, and only then call preventDefault — so
 * normal scrolling (and upward drags) are never interfered with. Never throws.
 */

import { router } from "@inertiajs/react";
import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment } from "./detection";

const SCROLLER_ID = "main_content";
const INDICATOR_ID = "tkc-ptr-indicator";
const TRIGGER_DISTANCE = 70; // px of travel past which release triggers a refresh
const MAX_PULL = 110; // cap on indicator travel
const RESISTANCE = 0.5; // the pull feels resisted (drag > travel)

function reportPtrError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_pull_to_refresh" },
    });
  } catch {
    // reporting must never break the app
  }
}

// --- Pure helpers (unit-tested) --------------------------------------------

/** Indicator travel for a raw downward drag; 0 for an upward/zero drag. */
export function pullDistance(
  deltaY,
  { resistance = RESISTANCE, max = MAX_PULL } = {},
) {
  if (deltaY <= 0) {
    return 0;
  }
  return Math.min(deltaY * resistance, max);
}

/** Whether releasing at this travel should trigger a refresh. */
export function shouldRefresh(distance, trigger = TRIGGER_DISTANCE) {
  return distance >= trigger;
}

// --- DOM ---------------------------------------------------------------------

function scroller() {
  return document.getElementById(SCROLLER_ID);
}

function ensureIndicator() {
  let el = document.getElementById(INDICATOR_ID);
  if (el) {
    return el;
  }
  el = document.createElement("div");
  el.id = INDICATOR_ID;
  el.setAttribute("aria-hidden", "true");
  el.style.cssText = [
    "position:fixed",
    "top:0",
    "left:50%",
    "z-index:2147483646",
    "width:28px",
    "height:28px",
    "margin-left:-14px",
    "border-radius:50%",
    "background:#323232",
    "box-shadow:0 1px 6px rgba(0,0,0,0.3)",
    "opacity:0",
    "pointer-events:none",
    // A simple rotating ring as the spinner.
    "border:2px solid rgba(255,255,255,0.35)",
    "border-top-color:#ffffff",
    "transform:translateY(-40px)",
  ].join(";");
  document.body.appendChild(el);
  return el;
}

function setIndicator(distance, spinning) {
  const el = ensureIndicator();
  el.style.opacity = distance > 4 ? "1" : "0";
  el.style.transform = `translateY(${distance - 34}px)`;
  el.style.animation = spinning ? "tkc-ptr-spin 0.7s linear infinite" : "none";
}

function hideIndicator() {
  const el = document.getElementById(INDICATOR_ID);
  if (el) {
    el.style.opacity = "0";
    el.style.animation = "none";
    el.style.transform = "translateY(-40px)";
  }
}

// Inject the spin keyframes once.
function ensureKeyframes() {
  if (document.getElementById("tkc-ptr-style")) {
    return;
  }
  const style = document.createElement("style");
  style.id = "tkc-ptr-style";
  style.textContent =
    "@keyframes tkc-ptr-spin{to{transform:translateY(36px) rotate(360deg)}}";
  document.head.appendChild(style);
}

let startY = 0;
let armed = false; // touchstart began at the top
let pulling = false; // actively dragging down from the top
let refreshing = false;
let currentDistance = 0;

function onTouchStart(event) {
  if (refreshing || event.touches.length !== 1) {
    armed = false;
    return;
  }
  const el = scroller();
  armed = !!el && el.scrollTop <= 0;
  pulling = false;
  currentDistance = 0;
  if (armed) {
    startY = event.touches[0].clientY;
  }
}

function onTouchMove(event) {
  if (!armed || refreshing) {
    return;
  }
  const el = scroller();
  if (!el || el.scrollTop > 0) {
    armed = false;
    pulling = false;
    hideIndicator();
    return;
  }
  const deltaY = event.touches[0].clientY - startY;
  if (deltaY <= 0) {
    // Dragging up (or not moving) — this is a normal scroll, stay out of it.
    pulling = false;
    hideIndicator();
    return;
  }
  pulling = true;
  // Stop the scroller from also scrolling while we own this downward pull.
  event.preventDefault();
  currentDistance = pullDistance(deltaY);
  setIndicator(currentDistance, false);
}

function triggerRefresh() {
  refreshing = true;
  setIndicator(TRIGGER_DISTANCE, true);
  const done = () => {
    refreshing = false;
    hideIndicator();
  };
  try {
    router.reload({ onFinish: done });
  } catch (error) {
    done();
    reportPtrError(error);
  }
}

function onTouchEnd() {
  if (refreshing) {
    return;
  }
  const wasPulling = pulling;
  const distance = currentDistance;
  armed = false;
  pulling = false;
  currentDistance = 0;
  if (wasPulling && shouldRefresh(distance)) {
    triggerRefresh();
  } else {
    hideIndicator();
  }
}

let registered = false;

/**
 * Wire pull-to-refresh on the main scroller. No-op off the Capacitor shell;
 * runs once; never throws. Listeners live on `document` so they survive React
 * re-mounts of `#main_content`.
 */
export function registerPullToRefresh() {
  if (!isCapacitorEnvironment() || registered) {
    return;
  }
  registered = true;
  try {
    ensureKeyframes();
    // touchmove must be non-passive so we can preventDefault the scroll while
    // pulling; it early-returns unless a top-anchored downward pull is active.
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    document.addEventListener("touchcancel", onTouchEnd, { passive: true });
  } catch (error) {
    registered = false;
    reportPtrError(error);
  }
}
